import React, { useState } from 'react';
import {
  Box, Chip, Collapse, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import ColetaDoHost from './ColetaDoHost';
import { fmtData } from './estilo';

/**
 * O conteudo de uma coleta como tabela de hosts.
 *
 * Cada linha e um equipamento; expandir mostra o que os parsers extrairam, e
 * dentro disso o texto cru fica atras do proprio expand. Tres niveis, do mais
 * resumido ao mais bruto — quem so quer saber "quantos vizinhos OSPF tem" para
 * na primeira camada; quem desconfia do parser desce ate a ultima.
 */
export default function TabelaDeHosts({
  coleta, hostsPorId, empresa, compacto = false,
  abertos: abertosProp, onAlternar, busca = '',
}) {
  const [abertosLocal, setAbertosLocal] = useState(() => new Set());

  // Controlado quando o pai passa `abertos`: e assim que os dois lados da
  // comparacao abrem o MESMO host juntos. Sem isso, expandir o pe01 a esquerda
  // deixava a direita fechada e desalinhava tudo — justamente o que a rolagem
  // sincronizada tenta evitar.
  const controlado = !!abertosProp;
  const abertos = controlado ? abertosProp : abertosLocal;

  const alternar = (hostId) => {
    if (controlado) { onAlternar(hostId); return; }
    setAbertosLocal((prev) => {
      const nova = new Set(prev);
      if (nova.has(hostId)) nova.delete(hostId);
      else nova.add(hostId);
      return nova;
    });
  };

  const termo = (busca || '').trim().toLowerCase();
  const hosts = (coleta?.hosts || []).filter((hid) => {
    if (!termo) return true;
    const h = hostsPorId[hid];
    return `${h?.host_name || hid} ${h?.ip_address || ''}`.toLowerCase().includes(termo);
  });
  const falhos = new Set(coleta?.failed_hosts || []);

  if (!hosts.length) {
    return (
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
        {termo ? `Nenhum host com “${busca}”.` : 'Esta coleta não tem hosts.'}
      </Typography>
    );
  }

  return (
    <Box>
      {!compacto && (
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
          Coleta #{coleta.run_id} · {fmtData(coleta.created_at)} · {coleta.outputs} comando(s)
          {' '}· {coleta.entities} item(ns)
        </Typography>
      )}

      {hosts.map((hid) => {
        const aberto = abertos.has(hid);
        const falhou = falhos.has(hid);
        const host = hostsPorId[hid];

        return (
          <Box key={hid} sx={{ mb: 0.5 }}>
            <Box
              onClick={() => alternar(hid)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.6, px: 1,
                bgcolor: aberto ? '#f1f5f9' : '#fafafa', cursor: 'pointer',
                borderLeft: `3px solid ${falhou ? '#c0392b' : '#16a085'}`,
              }}
            >
              <IconButton size="small" sx={{ p: 0.2 }}>
                {aberto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
              <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 140 }}>
                {host?.host_name || `Host ${hid}`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {host?.ip_address || ''}
              </Typography>
              {falhou && (
                <Tooltip title="Este host não respondeu nesta coleta">
                  <Chip size="small" label="sem resposta"
                    sx={{ height: 18, bgcolor: '#c0392b', color: '#fff' }} />
                </Tooltip>
              )}
            </Box>

            <Collapse in={aberto} unmountOnExit>
              <Box sx={{ pl: 3.5, pr: 1, py: 1 }}>
                <ColetaDoHost runId={coleta.run_id} hostId={hid} empresa={empresa} />
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
